import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { aplicarErrosDoServidor } from '@/api/erros';
import type { ProntuarioResponse } from '@/api/generated/model';
import { usePacientesControllerObter } from '@/api/generated/pacientes/pacientes';
import {
  getProntuariosControllerAnexosQueryKey,
  getProntuariosControllerListarQueryKey,
  useProntuariosControllerAdicionarAnexo,
  useProntuariosControllerAnexos,
} from '@/api/generated/prontuarios/prontuarios';
import {
  getTratamentosControllerListarQueryKey,
  getTratamentoVigenteControllerTratamentoVigenteQueryKey,
  useTratamentoVigenteControllerTratamentoVigente,
} from '@/api/generated/tratamentos/tratamentos';
import { ApiError } from '@/api/http-client';
import { ArquivosEExames } from '@/features/historico/components/ArquivosEExames';
import { EditorDeTratamento } from '@/features/historico/components/EditorDeTratamento';
import { type ItensSelecionados, itensValidos, NENHUM_ITEM } from '@/features/historico/itensDoProntuario';
import {
  anexoSchema,
  type FormularioProntuario,
  itensParaSincronizar,
  linhasDoTratamento,
} from '@/features/historico/prontuarioForm';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/**
 * O tratamento do anexo, como o servidor espera dentro do JSON `dados` —
 * AJUSTAR o vigente ou INICIAR um (ver `tratamentoDoAnexoSchema` no backend).
 * O multipart carrega `dados` como texto, então o cliente gerado não tipa isto.
 */
type TratamentoDoAnexo = { acao: 'AJUSTAR' | 'INICIAR' } & Record<string, unknown>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  prontuario: ProntuarioResponse;
}

/** O dia de HOJE, local — é a data do anexo, e a da vigência que um ajuste abre. */
function hoje(): string {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
}

function valoresIniciais(): FormularioProntuario {
  return {
    tipo: 'CONSULTA',
    titulo: '',
    conteudo: '',
    atendidoEm: '',
    declaracaoTratamento: '',
    tituloTratamento: '',
    evolucao: '',
    evolucaoObservacoes: '',
    medicamentos: [],
  };
}

/**
 * Novo anexo (item 16) — a ÚNICA forma de atualizar um prontuário depois de
 * registrado, e só no mais recente do paciente.
 *
 * Usa o MESMO formulário do prontuário (`FormularioProntuario`) para o
 * `EditorDeTratamento` servir aqui sem cópia: `titulo`/`conteudo` viram título
 * e texto do anexo, e `declaracaoTratamento === 'EM_TRATAMENTO'` é o
 * "registrar alteração de tratamento" ligado (ver `anexoSchema`).
 *
 * O tratamento vai NO MESMO pedido do anexo — o servidor grava os dois numa
 * transação só: ajuste sem o anexo que o explica, ou anexo sem o ajuste que
 * anuncia, seriam os dois um registro mentindo.
 */
export function NovoAnexoDialogo({ aberto, aoFechar, prontuario }: Props) {
  const queryClient = useQueryClient();
  const { pacienteId } = prontuario;
  const [itens, setItens] = useState<ItensSelecionados>(NENHUM_ITEM);
  const [arquivosEnviando, setArquivosEnviando] = useState(0);
  const [avisoVazio, setAvisoVazio] = useState(false);

  const vigente = useTratamentoVigenteControllerTratamentoVigente(
    pacienteId,
    { emData: hoje() },
    { query: { enabled: aberto } },
  );
  const tratamentoVigente = vigente.data?.tratamento ?? null;

  const paciente = usePacientesControllerObter(pacienteId, { query: { enabled: aberto } });
  const pesoKg = paciente.data?.pesoKg ?? null;

  // Exames que já estão no prontuário (cadastro + anexos anteriores) não voltam para escolha.
  const anexos = useProntuariosControllerAnexos(prontuario.id, { query: { enabled: aberto } });
  const examesJaVinculados = [
    ...prontuario.exames.map((e) => e.exameId),
    ...(anexos.data?.itens ?? []).flatMap((a) => a.exames.map((e) => e.exameId)),
  ];

  const form = useForm<FormularioProntuario>({
    resolver: zodResolver(anexoSchema(Boolean(tratamentoVigente))),
    defaultValues: valoresIniciais(),
  });
  const {
    formState: { errors, isSubmitting },
  } = form;
  const registrandoTratamento = form.watch('declaracaoTratamento') === 'EM_TRATAMENTO';

  const adicionar = useProntuariosControllerAdicionarAnexo();

  useEffect(() => {
    if (!aberto) return;
    form.reset(valoresIniciais());
    setItens(NENHUM_ITEM);
    setArquivosEnviando(0);
    setAvisoVazio(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  /** Liga o tratamento já com o que está valendo — quem não mexer, não registra ajuste nenhum. */
  function alternarTratamento(ligar: boolean) {
    form.reset({
      ...form.getValues(),
      declaracaoTratamento: ligar ? 'EM_TRATAMENTO' : '',
      tituloTratamento: ligar ? (tratamentoVigente?.titulo ?? '') : '',
      medicamentos: ligar && tratamentoVigente ? linhasDoTratamento(tratamentoVigente) : [],
    });
  }

  function montarTratamento(dados: FormularioProntuario): TratamentoDoAnexo | undefined {
    if (dados.declaracaoTratamento !== 'EM_TRATAMENTO') return undefined;
    const linhas = itensParaSincronizar(dados.medicamentos, pesoKg);

    if (tratamentoVigente) {
      return { acao: 'AJUSTAR', tratamentoId: tratamentoVigente.id, medicamentos: linhas };
    }

    const dia = hoje();
    return {
      acao: 'INICIAR',
      titulo: dados.tituloTratamento.trim(),
      medicamentos: linhas.map((item, ordem) => ({
        catalogoFarmacoId: item.catalogoFarmacoId,
        nomeLivre: item.nomeLivre,
        ordem,
        doseInicial: {
          vigenteDe: dia,
          doseValor: item.doseValor,
          doseUnidade: item.doseUnidade,
          dosePorKg: item.dosePorKg,
          pesoKgReferencia: item.pesoKgReferencia,
          frequencia: item.frequencia,
          frequenciaLivre: item.frequenciaLivre,
          via: item.via,
          apresentacaoId: item.apresentacaoId,
          nomeComercialLivre: item.nomeComercialLivre,
        },
      })),
    };
  }

  const aoEnviar = form.handleSubmit(async (dados) => {
    const conteudo = dados.conteudo.trim();
    const tratamento = montarTratamento(dados);
    const temItens = itens.arquivos.length > 0 || itens.exames.length > 0;
    if (!conteudo && !temItens && !tratamento) {
      setAvisoVazio(true);
      return;
    }

    try {
      // Anexo e arquivos num envio só — o servidor grava tudo ou nada.
      await adicionar.mutateAsync({
        id: prontuario.id,
        data: {
          dados: JSON.stringify({
            titulo: dados.titulo.trim(),
            ...(conteudo ? { conteudo } : {}),
            ...(itens.arquivos.length > 0 ? { titulosDosArquivos: itens.arquivos.map((a) => a.titulo.trim()) } : {}),
            ...(itens.exames.length > 0 ? { exameIds: itens.exames.map((e) => e.exameId) } : {}),
            ...(tratamento ? { tratamento } : {}),
          }),
          ...(itens.arquivos.length > 0 ? { arquivos: itens.arquivos.map((a) => a.arquivo!) } : {}),
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getProntuariosControllerAnexosQueryKey(prontuario.id) }),
        queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() }),
        ...(tratamento
          ? [
              queryClient.invalidateQueries({ queryKey: getTratamentosControllerListarQueryKey() }),
              queryClient.invalidateQueries({ queryKey: getTratamentoVigenteControllerTratamentoVigenteQueryKey(pacienteId) }),
            ]
          : []),
      ]);
      toast.success('Anexo adicionado ao prontuário.');
      aoFechar();
    } catch (erro) {
      // Outro atendimento foi registrado enquanto o diálogo estava aberto: este
      // prontuário deixou de ser o mais recente. Recarrega a lista e fecha.
      if (erro instanceof ApiError && erro.status === 409 && erro.message.includes('mais recente')) {
        toast.error(erro.message);
        await queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() });
        aoFechar();
        return;
      }
      aplicarErrosDoServidor(erro, form.setError, 'Não foi possível adicionar o anexo.');
    }
  });

  const podeSalvar = !isSubmitting && arquivosEnviando === 0 && itensValidos(itens);

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo anexo</DialogTitle>
          <DialogDescription>Registre uma atualização deste atendimento — texto, arquivos, exames ou tratamento.</DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Anexos não podem ser editados nem excluídos depois de salvos. Data, hora e autor ficam registrados.
            </AlertDescription>
          </Alert>

          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="titulo-anexo" rotulo="Título" erro={errors.titulo?.message} obrigatorio>
            <Input
              id="titulo-anexo"
              maxLength={120}
              placeholder="Ex.: Resultado do cortisol"
              className={CLASSE_CONTROLE}
              {...form.register('titulo')}
            />
          </Campo>

          <Campo id="conteudo-anexo" rotulo="Texto" dica="Opcional — a observação ou conduta nova.">
            <Textarea id="conteudo-anexo" rows={4} className={CLASSE_CONTROLE} {...form.register('conteudo')} />
          </Campo>

          <ArquivosEExames
            pacienteId={pacienteId}
            valor={itens}
            aoMudar={(novo) => {
              setItens(novo);
              setAvisoVazio(false);
            }}
            aoMudarEnviando={setArquivosEnviando}
            examesJaVinculados={examesJaVinculados}
          />

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-brand cursor-pointer">
              <Checkbox
                checked={registrandoTratamento}
                onCheckedChange={(v) => {
                  alternarTratamento(v === true);
                  setAvisoVazio(false);
                }}
                aria-label="Registrar alteração de tratamento"
              />
              Registrar alteração de tratamento
            </label>
            {registrandoTratamento && (
              <>
                <p className="text-xs text-muted-foreground">
                  {tratamentoVigente
                    ? 'O editor abre com o tratamento em curso. O que você mudar vale a partir de hoje.'
                    : 'Nenhum tratamento em curso — este anexo inicia um.'}
                </p>
                {!tratamentoVigente && (
                  <Campo id="titulo-tratamento" rotulo="Nome do tratamento" erro={errors.tituloTratamento?.message} obrigatorio>
                    <Input id="titulo-tratamento" className={CLASSE_CONTROLE} {...form.register('tituloTratamento')} />
                  </Campo>
                )}
                <EditorDeTratamento form={form} pesoKg={pesoKg} carregandoVigente={vigente.isFetching} />
              </>
            )}
          </div>

          {avisoVazio && (
            <p role="alert" className="text-sm text-destructive">
              Preencha o texto, anexe um arquivo, vincule um exame ou registre uma alteração de tratamento.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!podeSalvar}
              title={arquivosEnviando > 0 ? 'Aguarde o envio dos arquivos' : !itensValidos(itens) ? 'Dê um título a cada arquivo' : undefined}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar anexo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
